import { assign, chain, find, findIndex, get, indexOf, merge } from 'lodash';

import { Action } from '../actions';
import { UPDATE_CLIENT_MOBILE_APPS_DATA } from '../actions/clientMobileAppsData';
import {
    ADD_MOBILE_APP,
    APP_FLAT_SETTINGS_CHANGE,
    CLEAR_MOBILE_APPS_ERROR,
    FETCH_APP_DATA,
    FETCH_APP_INFO,
    FILL_APP_DATA,
    SHOW_FETCH_CREATE_APP_ERROR,
    SHOW_IN_AD_CHANGE,
    TRACKING_SYSTEM_CHANGE,
    UPDATE_APP_INFO,
    UPDATE_APP_TRACKERS_TEMPLATES,
    UPDATE_MOBILE_APP
} from '../actions/mobile-app';
import { UrlHelpers } from '../service/UrlHelpers';
import { ValidationResultItem } from '../typings/common';
import {
    AppTracker,
    AppTrackerParams,
    JavaAppTrackerTemplate,
    MobileAppPageState
} from '../typings/mobile-apps';
import { MobileAppsState, State } from '../typings/state';

const AppInfoInitial = {
    name: '',
    iconUrl: '',
    rating: 0,
    votes: 0,
    price: 0
};

const AppSettingsInitial = {
    link: '',
    name: '',
    showInAd: {},
    tracker: {},
    canEditMobileAppDomain: false,
    canViewMobileAppDomain: false
};

const trackerIdPlaceholder = '{tracker_id}';

export const AppInitialState: MobileAppsState = {
    pageState: MobileAppPageState.initial,
    appInfo: AppInfoInitial,
    appSettings: AppSettingsInitial,
    trackersTemplates: []
};

/**
 * Разбираем ссылку на базовую часть, включающую обязательные параметры
 * и выбранные параметры (пользовательские и опциональные)
 * @param tracker
 * @param template
 */
function trackingUrlParse(
    tracker: AppTracker,
    template?: JavaAppTrackerTemplate
): AppTracker {
    const { url, trackerId, userParams } = tracker;

    if (template && url && UrlHelpers.isUrlValid(url)) {
        const { parameters } = template;

        const baseUrlObj = new URL(url);
        const searchParams = baseUrlObj.searchParams;
        const selectedParams = [] as AppTrackerParams;
        const selectedUserParams = [] as AppTrackerParams;

        let baseUrl: string;
        let baseUrlWithId: string;

        searchParams.forEach((paramValue: string, paramName: string) => {
            const selectedParam = find(parameters, {
                name: paramName,
                required: false
            });
            const selectedUserParam = indexOf(userParams, paramName) !== -1;

            if (selectedParam) {
                selectedParams.push({
                    name: paramName,
                    value: paramValue
                });
            } else if (selectedUserParam) {
                selectedUserParams.push({
                    name: paramName,
                    value: paramValue
                });
            }
        });

        selectedParams.forEach(selParam => {
            searchParams.delete(selParam.name);
        });

        selectedUserParams.forEach(userParam => {
            searchParams.delete(userParam.name);
        });

        baseUrl = decodeURI(baseUrlObj.href);
        baseUrlWithId = decodeURI(baseUrlObj.href);

        if (trackerId && baseUrl.indexOf(trackerId) !== -1) {
            baseUrl = baseUrl.replace(trackerId, trackerIdPlaceholder);
        }

        return {
            baseUrl: baseUrl,
            baseUrlWithId: baseUrlWithId,
            selectedParams: selectedParams,
            selectedUserParams: selectedUserParams,
            urlEditable: template.editable
        };
    } else {
        return {
            baseUrl: url,
            selectedUserParams: [],
            selectedParams: []
        };
    }
}

/**
 * Формируем трекинговую ссылку из базовой ссылки
 * и выбранных параметров
 * @param osType
 * @param template
 * @param tracker
 */
function trackUrlBuild(
    tracker: AppTracker,
    template?: JavaAppTrackerTemplate,
    osType?: string
): AppTracker {
    const {
        url,
        baseUrl,
        trackerId,
        selectedParams,
        selectedUserParams
    } = tracker;
    let newUrl = url;
    let newBaseUrl = baseUrl;
    let newBaseUrlWithId = baseUrl;

    if (baseUrl && UrlHelpers.isUrlValid(baseUrl)) {
        const urlObj = new URL(baseUrl);
        const baseUrlObj = new URL(baseUrl);
        const searchValue = trackerIdPlaceholder;

        if (template && template.parameters) {
            // Удаляем старые обязательные параметры
            template.parameters.forEach(param => {
                if (param.required && param.osType !== osType) {
                    urlObj.searchParams.delete(param.name);
                    baseUrlObj.searchParams.delete(param.name);
                }
            });

            // Добавляем новые обязательные параметры
            template.parameters.forEach(param => {
                if (param.required && param.osType === osType) {
                    urlObj.searchParams.set(param.name, param.value);
                    baseUrlObj.searchParams.set(param.name, param.value);
                }
            });
        }

        (selectedParams || []).forEach(param => {
            urlObj.searchParams.set(param.name, param.value);
        });

        (selectedUserParams || []).forEach(param => {
            urlObj.searchParams.set(param.name, param.value);
        });

        // softURIDecode - чтобы не терялись спец. символы
        // Если url уже декодирова - возвращаем его же
        newUrl = softURIDecode(urlObj.href);
        newBaseUrl = softURIDecode(baseUrlObj.href);

        // обновляем url и базовую ссылку, если в них есть место для trackerId
        if (newBaseUrl.indexOf(searchValue) !== -1) {
            newBaseUrlWithId = newBaseUrl.replace(
                searchValue,
                trackerId || searchValue
            );
        }
        if (newUrl.indexOf(searchValue) !== -1) {
            newUrl = newUrl.replace(searchValue, trackerId || searchValue);
        }
    }

    return {
        url: newUrl,
        baseUrl: newBaseUrl,
        baseUrlWithId: newBaseUrlWithId
    };
}

function softURIDecode(url: string): string {
    try {
        return decodeURI(url);
    } catch (err) {
        return url;
    }
}

export function mobileAppsCreate(
    state: MobileAppsState = AppInitialState,
    action: Action,
    fullState: State
): MobileAppsState {
    switch (action.type) {
        case UPDATE_CLIENT_MOBILE_APPS_DATA:
            return {
                ...state,
                appSettings: {
                    ...state.appSettings,
                    canEditMobileAppDomain: get(
                        action,
                        'data.canEditMobileAppDomain'
                    ),
                    canViewMobileAppDomain: get(
                        action,
                        'data.canViewMobileAppDomain'
                    )
                }
            };
        case FETCH_APP_INFO:
        case FETCH_APP_DATA:
            return {
                ...state,
                pageState: MobileAppPageState.loading
            };
        case UPDATE_APP_INFO:
            return {
                ...state,
                pageState: MobileAppPageState.initial,
                appInfo: action.appInfo
                    ? action.appInfo
                    : AppInitialState.appInfo,
                appSettings: {
                    ...state.appSettings,
                    name: action.appInfo
                        ? action.appInfo.name
                        : AppInitialState.appInfo.name,
                    showInAd: action.appInfo
                        ? {
                              price: false,
                              rating: !!action.appInfo.rating,
                              votes: !!action.appInfo.votes,
                              icon: !!action.appInfo.iconUrl
                          }
                        : {},
                    tracker: action.appInfo
                        ? {
                              ...state.appSettings.tracker,
                              ...trackUrlBuild(
                                  state.appSettings.tracker,
                                  find(state.trackersTemplates, {
                                      trackingSystem:
                                          state.appSettings.tracker
                                              .trackingSystem
                                  }),
                                  action.appInfo.osType
                              )
                          }
                        : state.appSettings.tracker,
                    domain: action.appInfo && action.appInfo.publisherDomain
                },
                errors: []
            };
        case FILL_APP_DATA:
            const appData = action.app;

            if (appData) {
                const parsedTracker = trackingUrlParse(
                    appData.appSettings.tracker,
                    find(state.trackersTemplates, {
                        trackingSystem:
                            appData.appSettings.tracker.trackingSystem
                    })
                );

                appData.appSettings.tracker = assign(
                    {},
                    appData.appSettings.tracker,
                    parsedTracker
                );

                appData.appSettings.canEditMobileAppDomain = get(
                    state,
                    'appSettings.canEditMobileAppDomain',
                    false
                );

                appData.appSettings.canViewMobileAppDomain = get(
                    state,
                    'appSettings.canViewMobileAppDomain',
                    false
                );
            }

            return {
                ...state,
                pageState: MobileAppPageState.initial,
                appInfo: appData ? appData.appInfo : AppInfoInitial,
                appSettings: appData ? appData.appSettings : AppSettingsInitial,
                errors: []
            };
        case APP_FLAT_SETTINGS_CHANGE:
            return {
                ...state,
                appSettings: {
                    ...state.appSettings,
                    ...action.value
                },
                errors: state.errors
                    ? state.errors.filter(
                          ({ path }) =>
                              !Object.keys(action.value).includes(path)
                      )
                    : []
            };
        case SHOW_IN_AD_CHANGE:
            return {
                ...state,
                appSettings: {
                    ...state.appSettings,
                    showInAd: { ...state.appSettings.showInAd, ...action.value }
                }
            };
        case TRACKING_SYSTEM_CHANGE:
            const newTracker = assign(
                {},
                state.appSettings.tracker,
                action.tracker
            );
            const newTrackerUrl = trackUrlBuild(
                newTracker,
                find(state.trackersTemplates, {
                    trackingSystem: newTracker.trackingSystem
                }),
                state.appInfo.osType
            );
            const oldSelectedUserParams = get(
                state,
                'appSettings.tracker.selectedUserParams',
                []
            );
            const newSelectedUserParams = get(
                newTracker,
                'selectedUserParams',
                []
            );

            const errors = state.errors
                ? state.errors.filter(
                      ({ path }) =>
                          'trackers[0].url' !== path &&
                          'trackers[0].trackerId' !== path
                  )
                : [];
            const newErrors: Array<ValidationResultItem> = [];

            if (oldSelectedUserParams.length && errors.length) {
                errors.forEach(error => {
                    if (error.path.indexOf('userParams') !== 1) {
                        const oldParamIndex = chain(error.path)
                            .toPath()
                            .chunk(2)
                            .fromPairs()
                            .get('userParams')
                            .value();

                        const paramName = get(
                            oldSelectedUserParams,
                            `[${oldParamIndex}].name`
                        );

                        if (paramName) {
                            const newParamIndex = findIndex(
                                newSelectedUserParams,
                                { name: paramName }
                            );

                            if (newParamIndex !== -1) {
                                const newPath = `trackers[0].userParams[${newParamIndex}]`;

                                newErrors.push(merge(error, { path: newPath }));
                            }
                        }
                    } else {
                        newErrors.push(error);
                    }
                });
            }

            return {
                ...state,
                appSettings: {
                    ...state.appSettings,
                    tracker: {
                        ...newTracker,
                        ...newTrackerUrl
                    }
                },
                errors: newErrors
            };
        case SHOW_FETCH_CREATE_APP_ERROR:
            return {
                ...state,
                pageState: MobileAppPageState.initial,
                errors: action.errors
            };
        case CLEAR_MOBILE_APPS_ERROR:
            return {
                ...state,
                errors:
                    action.pathes.length && state.errors
                        ? state.errors.filter(
                              ({ path }) => !action.pathes.includes(path)
                          )
                        : []
            };
        case ADD_MOBILE_APP:
        case UPDATE_MOBILE_APP:
            return {
                ...state,
                pageState: MobileAppPageState.saving
            };
        case UPDATE_APP_TRACKERS_TEMPLATES:
            return {
                ...state,
                trackersTemplates: action.trackersTemplates
            };
        default:
            return state;
    }
}
